import {
  registerDecorator,
  ValidationArguments,
  ValidationOptions,
  ValidatorConstraint,
  ValidatorConstraintInterface,
} from 'class-validator';
import { Injectable } from '@nestjs/common';
import { EntityManager, EntityName } from '@mikro-orm/core';
import type { Primitive } from '@/common/types/global';

interface IsUniqueOptions {
  entity: EntityName<object>;
  column: string;
  ignoreIdPath?: string;
  where?: Record<string, any>;
}

@ValidatorConstraint({ async: true })
@Injectable()
export class IsUniqueConstraint implements ValidatorConstraintInterface {
  constructor(private readonly em: EntityManager) {}

  async validate(value: any, args: ValidationArguments): Promise<boolean> {
    const options: IsUniqueOptions = args.constraints[0] as IsUniqueOptions;
    const repo = this.em.getRepository(options.entity);

    const where: Record<string, any> = {
      [options.column]: value as Primitive,
      ...(options.where || {}),
    };

    if (options.ignoreIdPath) {
      const id = options.ignoreIdPath
        .split('.')
        .reduce(
          (obj, key) => obj?.[key] as string[],
          args.object,
        ) as unknown as number | undefined;
      if (id !== undefined) {
        where['id'] = { $ne: id };
      }
    }

    const existing = await repo.findOne(where);
    return !existing;
  }

  defaultMessage(args: ValidationArguments): string {
    const options: IsUniqueOptions = args.constraints[0] as IsUniqueOptions;
    return `${options.column} must be unique`;
  }
}

export function IsUnique(
  options: IsUniqueOptions,
  validationOptions?: ValidationOptions,
) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      name: 'IsUnique',
      target: object.constructor,
      propertyName,
      constraints: [options],
      options: validationOptions,
      validator: IsUniqueConstraint,
    });
  };
}
